import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import {
  addDealAddOn,
  createAddOnCatalogItem,
  deleteAddOnCatalogItem,
  deleteDealAddOn,
  fetchAddOnCatalog,
  fetchDealAddOns,
  updateAddOnCatalogItem,
  updateDealAddOn,
} from './addon-api'
import type { AddOnCatalogWriteBody, DealAddOnWriteBody } from '@/types/addon'

// --- Query key factory (cache contract: list = ["<entity>"], detail = ["<entity>", id]) ---

export const ADDON_KEYS = {
  catalog: (filters?: Record<string, unknown>) => ['addon-catalog', { filters }] as const,
  dealAddOns: (dealId: string) => ['deal-addons', dealId] as const,
}

// --- Outbox keys (see apps/frontend/AGENTS.md "offline outbox") ---

export const ADDON_CATALOG_CREATE_KEY = ['outbox', 'addon-catalog', 'create'] as const
export const ADDON_CATALOG_UPDATE_KEY = ['outbox', 'addon-catalog', 'update'] as const
export const ADDON_CATALOG_DELETE_KEY = ['outbox', 'addon-catalog', 'delete'] as const
export const DEAL_ADDON_ADD_KEY = ['outbox', 'deal-addons', 'add'] as const
export const DEAL_ADDON_UPDATE_KEY = ['outbox', 'deal-addons', 'update'] as const
export const DEAL_ADDON_DELETE_KEY = ['outbox', 'deal-addons', 'delete'] as const

// Replay functions must mirror the hook mutationFn exactly (same imported
// function) so an offline replay behaves identically to an online submit.
queryClient.setMutationDefaults(ADDON_CATALOG_CREATE_KEY, { mutationFn: createAddOnCatalogItem })
queryClient.setMutationDefaults(ADDON_CATALOG_UPDATE_KEY, {
  mutationFn: ({ id, body }: { id: string; body: Partial<AddOnCatalogWriteBody> }) =>
    updateAddOnCatalogItem(id, body),
})
queryClient.setMutationDefaults(ADDON_CATALOG_DELETE_KEY, { mutationFn: deleteAddOnCatalogItem })
queryClient.setMutationDefaults(DEAL_ADDON_ADD_KEY, {
  mutationFn: ({ dealId, body }: { dealId: string; body: DealAddOnWriteBody }) =>
    addDealAddOn({ dealId, body }),
})
queryClient.setMutationDefaults(DEAL_ADDON_UPDATE_KEY, {
  mutationFn: ({ lineId, body }: { lineId: string; body: Partial<DealAddOnWriteBody> }) =>
    updateDealAddOn(lineId, body),
})
queryClient.setMutationDefaults(DEAL_ADDON_DELETE_KEY, { mutationFn: deleteDealAddOn })

// --- Catalog ---

// filters === undefined disables the query: the deal page mounts the picker
// with the dialog closed, and an unguarded hook would fetch the whole
// catalog on every deal view.
export const useAddOnCatalog = (filters?: {
  q?: string
  category?: string
  boat_type?: string
  active?: boolean
}) => {
  return useQuery({
    queryKey: ADDON_KEYS.catalog(filters),
    queryFn: () => fetchAddOnCatalog(filters),
    enabled: filters !== undefined,
  })
}

export const useCreateAddOnCatalogItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ADDON_CATALOG_CREATE_KEY,
    mutationFn: createAddOnCatalogItem,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addon-catalog'] }),
  })
}

export const useUpdateAddOnCatalogItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ADDON_CATALOG_UPDATE_KEY,
    mutationFn: ({ id, body }: { id: string; body: Partial<AddOnCatalogWriteBody> }) =>
      updateAddOnCatalogItem(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addon-catalog'] }),
  })
}

export const useDeleteAddOnCatalogItem = () => {
  const qc = useQueryClient()
  return useMutation({
    mutationKey: ADDON_CATALOG_DELETE_KEY,
    mutationFn: deleteAddOnCatalogItem,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['addon-catalog'] }),
  })
}

// --- Deal add-on lines ---

export const useDealAddOns = (dealId: string) => {
  return useQuery({
    queryKey: ADDON_KEYS.dealAddOns(dealId),
    queryFn: () => fetchDealAddOns(dealId),
    enabled: !!dealId,
  })
}

// Line changes move the deal's aggregate value on the backend, so every
// mutation invalidates the deal (detail + lists) too.
const useInvalidateAddOnsAndDeal = () => {
  const qc = useQueryClient()
  return (dealId: string) => {
    qc.invalidateQueries({ queryKey: ['deal-addons', dealId] })
    qc.invalidateQueries({ queryKey: ['deals'] })
  }
}

// The dealId rides in the mutation variables (injected here, not via a
// closure) so an offline replay can rebuild the request path after a reload.
export const useAddDealAddOn = (dealId: string) => {
  const invalidate = useInvalidateAddOnsAndDeal()
  const mutation = useMutation<
    Awaited<ReturnType<typeof addDealAddOn>>,
    Error,
    { dealId: string; body: DealAddOnWriteBody }
  >({
    mutationKey: DEAL_ADDON_ADD_KEY,
    mutationFn: ({ dealId: dId, body }) => addDealAddOn({ dealId: dId, body }),
    onSuccess: (_data, vars) => invalidate(vars.dealId),
  })
  return {
    ...mutation,
    mutate: (body: DealAddOnWriteBody, options?: Parameters<typeof mutation.mutate>[1]) =>
      mutation.mutate({ dealId, body }, options),
    mutateAsync: (body: DealAddOnWriteBody, options?: Parameters<typeof mutation.mutateAsync>[1]) =>
      mutation.mutateAsync({ dealId, body }, options),
  }
}

export const useUpdateDealAddOn = (dealId: string) => {
  const invalidate = useInvalidateAddOnsAndDeal()
  return useMutation({
    mutationKey: DEAL_ADDON_UPDATE_KEY,
    mutationFn: ({ lineId, body }: { lineId: string; body: Partial<DealAddOnWriteBody> }) =>
      updateDealAddOn(lineId, body),
    onSuccess: () => invalidate(dealId),
  })
}

export const useDeleteDealAddOn = (dealId: string) => {
  const invalidate = useInvalidateAddOnsAndDeal()
  return useMutation({
    mutationKey: DEAL_ADDON_DELETE_KEY,
    mutationFn: deleteDealAddOn,
    onSuccess: () => invalidate(dealId),
  })
}
